<script>
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";

export default {
  name: "StandardV2BannerCarousel",
  components: {
    Swiper,
    SwiperSlide
  },
  props: {
    bannerList: {
      default: null,
      type: Array
    },
    dataTestPrefix: {
      default: "profile-banner-carousel-v2",
      type: String
    },
    profile: {
      default: () => ({}),
      type: Object
    }
  },
  data() {
    return {
      activeIndex: 0,
      interval: 5000,
      internalBannerList: [],
      modules: [Autoplay, Pagination],
      swiper: null
    };
  },
  computed: {
    resolvedBannerList() {
      return this.bannerList || this.internalBannerList;
    },
    resolvedProfile() {
      return Object.keys(this.profile).length
        ? this.profile
        : this.$store.state.shortProfile || {};
    },
    hasMultipleBanners() {
      return this.resolvedBannerList.length > 1;
    },
    autoplayConfig() {
      return {
        delay: this.interval,
        disableOnInteraction: true
      };
    }
  },
  watch: {
    async activeIndex(val, oldVal) {
      await this.handleActiveVideo(val, oldVal);
    },
    resolvedBannerList: {
      async handler() {
        this.activeIndex = 0;
        await this.$nextTick();
        await this.handleActiveVideo(0);
      },
      deep: true
    },
    interval() {
      if (!this.swiper || !this.swiper.autoplay) return;

      this.swiper.params.autoplay.delay = this.interval;
      this.swiper.autoplay.start();
    }
  },
  async created() {
    await this.getBannerList();
  },
  async mounted() {
    await this.handleActiveVideo(0);
  },
  beforeUnmount() {
    // this.pauseAllVideos();
  },
  methods: {
    async getBannerList() {
      if (this.bannerList) return;

      try {
        const data = await this.$axios.get("/api/v1/banner/campaign_form");
        if (data.result == 1 && data.detail.totalCount > 0) {
          this.internalBannerList = data.detail.detail;
          return;
        }
      } catch (_) {
        // Keep banner hidden when campaign banner loading fails.
      }

      this.internalBannerList = [];
    },
    onSwiper(swiper) {
      this.swiper = swiper;
    },
    onSlideChange(swiper) {
      this.activeIndex = swiper.realIndex || 0;
    },
    async handleActiveVideo(val, oldVal) {
      const activeBanner = this.resolvedBannerList[val];
      const oldBanner = this.resolvedBannerList[oldVal];

      if (oldBanner && oldBanner.id !== activeBanner?.id) {
        this.pauseAllVideos();
      }

      if (!activeBanner?.is_video) {
        this.interval = 5000;
        return;
      }

      await this.$nextTick();

      const video = this.activeVideo() || this.videoRef(activeBanner.id);
      if (!video) return;

      if (video.ended) {
        video.currentTime = 0;
      }

      video.play().catch(() => {
        // ignore autoplay rejection (e.g. browsers requiring user gesture)
      });
      video.onplay = () => {
        if (video.duration && Number.isFinite(video.duration)) {
          this.interval = Math.min(video.duration * 1000, 60000);
        }
      };
    },
    pauseAllVideos() {
      const videos = this.$el.querySelectorAll("video");
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    },
    pauseVideo(id) {
      const video = this.videoRef(id);
      if (!video) return;

      video.pause();
      video.currentTime = 0;
    },
    videoRef(id) {
      const ref = this.$refs[`video-${id}`];
      if (Array.isArray(ref)) {
        return ref[0];
      }
      return ref;
    },
    activeVideo() {
      const activeSlide = this.swiper?.slides?.[this.swiper.activeIndex];
      if (!activeSlide) return null;

      return activeSlide.querySelector("video");
    },
    getForm(item) {
      switch (item.mobile_redirect_type_id) {
        case 5: {
          const raw = item.redirect_id || "";
          const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
          if (!/^https?:\/\//i.test(candidate)) break;
          window.location.href = candidate;
          break;
        }
        case 6: {
          try {
            const url = new URL(item.redirect_url);
            const parameter = url.searchParams.get("urlkey");
            this.$router.push(
              `/form?urlKey=${parameter}&path=${item.redirect_url}&guid=${this.resolvedProfile.user_guid}`
            );
          } catch (_) {
            // ignore malformed redirect_url
          }
          break;
        }
        case 7:
          this.$router.push(
            `/reward?title=${item.path_name || this.$t("redeem_text")}`
          );
          break;
        default:
          break;
      }
    }
  }
};
</script>

<template>
  <div
    class="banner-carousel-v2"
    :data-test="dataTestPrefix"
    v-if="resolvedBannerList.length"
  >
    <Swiper
      class="banner-carousel-v2__swiper"
      :modules="modules"
      :slides-per-view="'auto'"
      :centered-slides="true"
      :loop="hasMultipleBanners"
      :pagination="{ clickable: true }"
      :autoplay="autoplayConfig"
      :allow-touch-move="true"
      :space-between="12"
      @swiper="onSwiper"
      @slide-change="onSlideChange"
    >
      <SwiperSlide
        v-for="item in resolvedBannerList"
        :key="item.id"
        class="banner-carousel-v2__slide"
        :data-test="`${dataTestPrefix}-item-${item.id}`"
      >
        <video
          v-if="item.is_video == 1"
          class="banner-carousel-v2__media"
          muted
          controls
          playsinline
          :ref="'video-' + item.id"
          :data-test="`${dataTestPrefix}-video`"
        >
          <source :src="item.image_url" type="video/mp4" />
        </video>
        <button
          v-else
          type="button"
          class="banner-carousel-v2__image-button"
          :data-test="`${dataTestPrefix}-image-btn`"
          @click="getForm(item)"
        >
          <c-img-lazy
            class="banner-carousel-v2__image"
            :src="item.image_url"
            ratio="1.7777777778"
            :data-test="`${dataTestPrefix}-image`"
          />
        </button>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="scss" scoped>
.banner-carousel-v2 {
  width: 100%;
  overflow: hidden;
  margin-bottom: 0.5rem;
  padding-bottom: 1rem;
}

.banner-carousel-v2__swiper {
  width: 100%;
  // padding: 0 0 26px;
  overflow: visible;
}

.banner-carousel-v2__slide {
  width: 100%;
  // max-width: 360px;
}

.banner-carousel-v2__media,
.banner-carousel-v2__image-button {
  display: block;
  width: 100%;
  // aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 0;
  // border-radius: 8px;
  background: transparent;
  padding: 0;
}

.banner-carousel-v2__media {
  object-fit: cover;
}

.banner-carousel-v2__image {
  width: 100%;
  height: 100%;
}

.banner-carousel-v2__image :deep(.v-img) {
  width: 100%;
  height: 100%;
}

.banner-carousel-v2 :deep(.swiper-pagination) {
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /* gap: 6px; */
}

.banner-carousel-v2 :deep(.swiper-pagination-bullet) {
  width: 10px;
  height: 4px;
  margin: 0;
  border-radius: 999px;
  background: #d9d9d9;
  opacity: 1;
  transition: width 0.2s ease, background-color 0.2s ease;
}

.banner-carousel-v2 :deep(.swiper-pagination-bullet-active) {
  width: 22px;
  background: var(--primary-color);
}
</style>
